import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calculator } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { JsonLd, SITE_URL } from "../components/SEO";
import HeroWheel from "../components/home/HeroWheel";
import CompareCalculator, { CompareInfo } from "../components/home/CourierCompare";
import TrustStrip from "../components/home/TrustStrip";
import WhyShipAny from "../components/home/WhyShipAny";
import CourierEngine from "../components/home/CourierEngine";
import CoverageMaps from "../components/home/CoverageMaps";
import D2CSection from "../components/home/D2CSection";
import DashboardPreview from "../components/home/DashboardPreview";
import InternationalSection from "../components/home/InternationalSection";
import NdrRtoSection from "../components/home/NdrRtoSection";
import CourierNetwork from "../components/home/CourierNetwork";
import HowItWorks from "../components/home/HowItWorks";
import AnalyticsSection from "../components/home/AnalyticsSection";
import TestimonialsSection from "../components/home/TestimonialsSection";
import FinalCta from "../components/home/FinalCta";
import FaqSection from "../components/home/FaqSection";

export default function Home() {
  const homeJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "ShipAny",
        url: SITE_URL,
        logo: `${SITE_URL}/og-image.png`,
        description: "Indian logistics platform for domestic and international shipping, courier rate comparison, labels, tracking, COD, NDR and returns.",
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: "ShipAny",
        publisher: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en-IN",
      },
    ],
  };
  return (
    <MarketingLayout>
      <JsonLd data={homeJsonLd} />
      {/* ============ CINEMATIC HERO ============ */}
      <section className="relative overflow-hidden bg-[#030B18]" data-testid="hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_75%_65%_at_72%_38%,#0B1E3D_0%,#071B3A_42%,#030B18_100%)]" />
        <div className="pointer-events-none absolute inset-0 hero-grid opacity-40" />
        <div className="pointer-events-none absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-[#0757D9]/20 blur-[120px]" />
        <div className="pointer-events-none absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-[#FF9933]/10 blur-[120px]" />

        {/* logistics wheel — behind content, right-aligned (desktop only; hidden on mobile to keep the headline legible) */}
        <div className="pointer-events-auto absolute -right-24 top-1/2 hidden -translate-y-1/2 opacity-90 md:block lg:right-4">
          <HeroWheel />
        </div>

        <div className="relative mx-auto flex min-h-[92vh] max-w-7xl flex-col justify-center px-5 pb-40 pt-28 sm:pb-44">
          <div className="max-w-2xl">
            <motion.span initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="mono inline-flex items-center gap-2 rounded-full border border-[#FF9933]/40 bg-[#FF9933]/10 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#FFB25E]">
              <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-[#10B981]" /> India's Smart Multi-Courier Shipping Platform
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display mt-6 text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              SHIP ANYWHERE.<br />
              <span className="text-saffron-green">SHIP SMARTER.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.22 }}
              className="mt-5 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
              Compare courier rates, choose the right delivery partner, generate shipping labels, track every shipment and manage COD — all from one powerful dashboard.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.34 }}
              className="mt-8 flex flex-col gap-3.5 sm:flex-row">
              <Link to="/signup" data-testid="hero-start-shipping-btn"
                className="group flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-4 font-display text-sm font-bold text-[#071B3A] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_44px_rgba(255,153,51,0.45)]">
                START SHIPPING FREE <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a href="#rate-calculator" data-testid="hero-calculate-rate-btn"
                className="flex items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/5 px-8 py-4 font-display text-sm font-bold text-white backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10">
                <Calculator className="h-4 w-4 text-shipany-cyan" /> CALCULATE SHIPPING
              </a>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
              className="mono mt-6 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 sm:text-xs">
              Domestic • International • B2B • D2C • COD • Bulk Shipping
            </motion.p>
          </div>
        </div>
      </section>

      {/* ============ FLOATING RATE CALCULATOR ============ */}
      <div id="rate-calculator" className="relative z-20 mx-auto -mt-32 max-w-5xl scroll-mt-24 px-5 sm:-mt-36">
        <motion.div initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.6 }}>
          <CompareCalculator />
        </motion.div>
      </div>

      <CompareInfo />

      <div className="pt-14"><TrustStrip /></div>
      <WhyShipAny />
      <CourierEngine />
      <CoverageMaps />
      <D2CSection />
      <DashboardPreview />
      <InternationalSection />
      <NdrRtoSection />
      <CourierNetwork />
      <HowItWorks />
      <AnalyticsSection />
      <TestimonialsSection />
      <FinalCta />
      <FaqSection />
    </MarketingLayout>
  );
}
